import { ref } from 'vue';
import type { Router } from 'vue-router';

// A deploy replaces the hashed files under /assets and the server keeps no old
// ones, so a tab opened before the deploy can no longer lazy-load a route chunk:
// the import fails and Vue Router silently aborts the navigation (links "do
// nothing"). Two defences:
//   1. a failed chunk load reloads the page straight to the route being opened;
//   2. index.html is polled, and a changed entry script raises a "reload" banner
//      before the user ever hits a dead link.

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
// If a reload did not cure the failure (server down, real bug), do not loop.
const RELOAD_GUARD_MS = 30 * 1000;
const RELOAD_GUARD_KEY = 'staleChunkReloadAt';

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Expected a JavaScript.*MIME type/i;

const updateAvailable = ref(false);

const ENTRY_SCRIPT_SELECTOR = 'script[type="module"][src]';

// Captured at load: the entry script this tab is actually running.
const loadedEntry = document.querySelector(ENTRY_SCRIPT_SELECTOR)?.getAttribute('src') ?? null;

async function checkForUpdate() {
  if (updateAvailable.value || !loadedEntry) {
    return;
  }
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}index.html`, { cache: 'no-store' });
    if (!res.ok) {
      return;
    }
    const latestDoc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const latest = latestDoc.querySelector(ENTRY_SCRIPT_SELECTOR)?.getAttribute('src');
    if (latest && latest !== loadedEntry) {
      updateAvailable.value = true;
    }
  } catch {
    // Offline or a transient server error: the next tick tries again.
  }
}

let started = false;

/** Begin polling for a newer deploy. No-op in dev, where there is no hashed entry. */
function startUpdateChecks() {
  if (started || !import.meta.env.PROD) {
    return;
  }
  started = true;
  setInterval(checkForUpdate, CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate();
    }
  });
  // Vite fires this when a dynamic import's preload (JS/CSS) fails.
  window.addEventListener('vite:preloadError', () => {
    updateAvailable.value = true;
  });
}

/**
 * When a route chunk is gone (new deploy), loads `targetPath` with a full page
 * load, which fetches the new index.html and its chunks. Returns false for
 * errors that are not chunk-load failures.
 */
function reloadOnStaleChunk(error: unknown, targetPath: string): boolean {
  const message = error instanceof Error ? error.message : String(error);
  if (!CHUNK_ERROR_RE.test(message)) {
    return false;
  }
  updateAvailable.value = true;
  const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
  if (Date.now() - lastReload < RELOAD_GUARD_MS) {
    return true; // reloaded moments ago and still failing: leave the banner up
  }
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  window.location.assign(targetPath);
  return true;
}

/**
 * Call once at app startup, next to the router: polls for a newer deploy and,
 * when navigation fails on a stale route chunk, reloads straight to the target
 * route. Registering a router error handler suppresses Vue Router's default
 * console.error, so that is kept for every other error.
 */
export function installAppUpdateChecks(router: Router) {
  startUpdateChecks();
  router.onError((error, to) => {
    if (!reloadOnStaleChunk(error, to.fullPath)) {
      console.error(error);
    }
  });
}

export function useAppUpdate() {
  return { updateAvailable };
}
